import { useState } from "react";
import { Pencil } from "lucide-react";
import { Button, Input } from "@/components/ui";
import { trpc } from "@/lib/trpc";
import { useAppStore } from "@/stores/app.store";

/**
 * Nummernkreise: Format von Rechnungs-/Angebotsnummern etc. Gilt nur für künftig vergebene Nummern;
 * bereits ausgestellte Dokumente behalten ihre Nummer, der Zähler läuft weiter.
 */
export function NumbersTab() {
  const utils = trpc.useUtils();
  const addToast = useAppStore((s) => s.addToast);
  const overview = trpc.numbering.overview.useQuery();
  const [editing, setEditing] = useState<string | null>(null);
  const [format, setFormat] = useState("");

  const preview = trpc.numbering.preview.useQuery(
    { docType: (editing ?? "invoice") as "invoice", format },
    { enabled: !!editing && format.length > 0 },
  );
  const save = trpc.numbering.setFormat.useMutation({
    onSuccess: (r) => {
      utils.numbering.overview.invalidate();
      setEditing(null);
      addToast({
        type: "success",
        title: "Nummernformat gespeichert",
        description: `Nächste Nummer: ${r.nextNumber}`,
      });
    },
    onError: (e) =>
      addToast({ type: "error", title: "Fehler", description: e.message }),
  });

  return (
    <div className="p-6 max-w-3xl space-y-6">
      <div>
        <h2 className="text-sm font-semibold text-text-primary mb-1">
          Nummernkreise
        </h2>
        <p className="text-xs text-text-tertiary">
          Eine Änderung gilt nur für <strong>künftig</strong> vergebene Nummern.
          Bereits ausgestellte Dokumente behalten ihre Nummer, der Zähler läuft
          weiter (die Rechnungsnummer muss laut § 11 UStG fortlaufend und
          einmalig bleiben). Das Zählen beginnt jedes Jahr wieder bei 1.
        </p>
      </div>

      <div className="card divide-y divide-border-subtle">
        {(overview.data ?? []).map((o) => (
          <div key={o.docType} className="p-4">
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="text-sm font-medium text-text-primary">
                  {o.label}
                </p>
                <p className="text-xs text-text-tertiary mt-0.5">
                  Format <code className="text-text-secondary">{o.format}</code>{" "}
                  · bisher {o.lastValue} in diesem Jahr · nächste Nummer{" "}
                  <strong className="text-text-primary">{o.nextNumber}</strong>
                </p>
              </div>
              {editing !== o.docType && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setEditing(o.docType);
                    setFormat(o.format);
                  }}
                >
                  <Pencil className="w-3.5 h-3.5" /> Ändern
                </Button>
              )}
            </div>

            {editing === o.docType && (
              <div className="mt-3 space-y-2">
                <Input
                  label="Format"
                  value={format}
                  onChange={(e) => setFormat(e.target.value)}
                  hint="Platzhalter: {YYYY} = 2026, {YY} = 26, {SEQ} = laufende Nummer ohne Nullen, {SEQ:4} = mit Nullen auf 4 Stellen. Beispiel: RE{YY}{SEQ} → RE264, RE2610, RE26100"
                />
                <p className="text-xs text-text-secondary">
                  {preview.data?.valid
                    ? `Vorschau: nächste Nummer ${preview.data.next} · danach z. B. ${preview.data.afterThat}`
                    : format
                      ? "Ungültiges Format (erlaubt: Buchstaben, Ziffern, - _ / . und die Platzhalter; {SEQ} ist Pflicht)."
                      : ""}
                </p>
                {(o.docType === "invoice" || o.docType === "credit_note") && (
                  <p className="text-xs text-warning">
                    Rechnungsnummern müssen eindeutig und fortlaufend sein.
                    Bereits ausgestellte Rechnungen bleiben unverändert.
                  </p>
                )}
                <div className="flex justify-end gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setEditing(null)}
                  >
                    Abbrechen
                  </Button>
                  <Button
                    variant="primary"
                    size="sm"
                    loading={save.isPending}
                    disabled={!preview.data?.valid || format === o.format}
                    onClick={() =>
                      save.mutate({ docType: o.docType as "invoice", format })
                    }
                  >
                    Format speichern
                  </Button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
      <p className="text-xs text-text-tertiary">
        Kundennummern werden nach Datum vergeben (TTMMJJ + Tageszähler ab 0), z.
        B. <code>0410260</code>, dann <code>0410261</code> usw. Bereits
        vergebene Kundennummern bleiben unverändert.
      </p>
    </div>
  );
}
